import React, { useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useFormik, Form, FormikProvider } from 'formik'
import { sendConfirmationEmailAction, clearSignUpErrors } from '../../../reducers/sign-up/action.js'

// material
import { Grid, Alert, Box, Checkbox, Typography } from '@material-ui/core'
import { LoadingButton } from '@material-ui/lab'
// routes
//import { PATH_AUTH } from '../../../routes/paths'
import useIsMountedRef from '../../../hooks/useIsMountedRef'
import translator from '../../../translate'
import { Link } from '@material-ui/core'
import { CoreTextField } from 'src/components/core'
// ----------------------------------------------------------------------

const RegisterForm = props => {
  const isMountedRef = useIsMountedRef()
  const isEmailInvalidError = useSelector(state => state.signUp?.emailInvalidError || false)
  const isUserExistsError = useSelector(state => state.signUp?.userExistsError || false)
  const hasErrors = isEmailInvalidError || isUserExistsError
  const submittedEmailRef = useRef(null)

  const formik = useFormik({
    initialValues: {
      email: '',
      terms: false,
    },
    //validationSchema: LoginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const { dispatch } = props
      const email = values.email
      if (email !== '' && email !== null) {
        submittedEmailRef.current = email
        const data = {
          email: email,
          application: 'becompe',
        }
        dispatch(sendConfirmationEmailAction(data))
      }
      if (isMountedRef.current) {
        setSubmitting(false)
      }
    },
  })

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
  } = formik

  const handleEmailChange = useCallback((e) => {
    formik.handleChange(e)
    if (hasErrors && submittedEmailRef.current !== null && e.target.value !== submittedEmailRef.current) {
      props.dispatch(clearSignUpErrors())
      submittedEmailRef.current = null
    }
  }, [hasErrors, formik.handleChange, props.dispatch])

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Box>
          {errors.afterSubmit && (
            <Alert severity="error">{translator._(errors.afterSubmit)}</Alert>
          )}

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Email
          </Typography>
          <CoreTextField
            fullWidth
            marginTop={false}
            margin="none"
            autoComplete="username"
            type="email"
            placeholder="Enter your email"
            {...getFieldProps('email')}
            onChange={handleEmailChange}
            error={Boolean((touched.email && errors.email) || isEmailInvalidError || isUserExistsError)}
            helperText={touched.email && errors.email}
            sx={{ m: 0, mt: 0 }}
          />

          {isEmailInvalidError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              This email address can't be used. Temporary or disposable email services are not supported. Please use a different email.
            </Alert>
          )}

          {isUserExistsError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              This e-mail has been already used.
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '20px', marginBottom: { xs: '24px', md: '40px' } }}>
            <Checkbox
              {...getFieldProps('terms')}
              checked={values.terms}
              sx={{ pl: 0, '&.Mui-checked': { color: 'primary.main' } }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              I agree to the{' '}
              <Link underline="always" color="primary" href="/terms-of-service" target="_blank">
                terms
              </Link>{' '}
              and{' '}
              <Link underline="always" color="primary" href="/privacy-policy" target="_blank">
                privacy policy
              </Link>
              .
            </Typography>
          </Box>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!values.terms || !values.email}
            sx={{
              height: '48px',
              padding: '15px 22px',
              backgroundColor: values.terms && values.email ? '#015ffb' : '#919eab',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: values.terms && values.email ? '#0151D5' : '#919eab',
                boxShadow: 'none',
              },
            }}
          >
            Continue
          </LoadingButton>
        </Box>

        <Box sx={{ marginTop: '16px', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Already have an account?{' '}
            <Link
              underline="none"
              variant="subtitle2"
              href="/login"
              sx={{
                color: '#015ffb',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Form>
    </FormikProvider>
  )
}

export default RegisterForm
